/* eslint-disable max-lines */
/* eslint-disable simple-import-sort/imports */
/* eslint-disable react/jsx-indent */
/* eslint-disable max-len */
import { FC, useCallback, useEffect, useState } from 'react';

import { Checkbox, Input, ScrollShadow, Select, SelectItem, Switch, Textarea } from '@heroui/react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { ArrowLeft, ArrowRight, Check, Trash2, Edit, Plus, Upload, X } from 'lucide-react';
import { UploadButton } from '@bytescale/upload-widget-react';

import { inputProps, selectProps } from '../../../../utils';
import { AttractionsFirestoreDocumentRequest, AttractionType } from '../../../../types';
import { useAuthState, useLangState } from '../../../../state';
import { AddressAutocomplete } from '../../../Organizations/components/CreateOrganizationWizard/AddressAutocomplete';

interface AttractionWizardProps {
  onSubmit: (data: AttractionsFirestoreDocumentRequest) => void;
  initialValues?: Partial<AttractionsFirestoreDocumentRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];
const GOOGLE_MAPS_API_KEY = 'AIzaSyBfROJsmdp_wGan1h6lRe3H9C5bSCtQZxo';
const MAP_CONTAINER_STYLE = { width: '100%', height: '350px' };
const DEFAULT_CENTER = { lat: 52.2297, lng: 21.0122 };
const BYTESCALE_API_KEY = import.meta.env.REACT_APP_BYTESCALE_API_KEY || '';

const ATTRACTION_TYPES = [
  { value: 'FORKIDS', label: 'For Kids' },
  { value: 'TRIP', label: 'Trip' },
  { value: 'CULTURE', label: 'Culture' },
  { value: 'FOODS', label: 'Food' },
];

const STEP_LABELS: { label: string; desc: string; optional?: boolean }[] = [
  { label: 'Basic Information', desc: 'Name and description' },
  { label: 'Location', desc: 'Address and map' },
  { label: 'Media', desc: 'Photos and gallery' },
  { label: 'Discount', desc: 'Promotion details', optional: true },
];

// Styled components (inline styles for now)
const WizardLayout = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: 'flex', gap: '32px', padding: '24px', paddingBottom: '96px', maxWidth: '1200px', margin: '0 auto' }}>
    {children}
  </div>
);

const StepperCard = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      width: '300px',
      flexShrink: 0,
      position: 'sticky',
      top: '24px',
      alignSelf: 'flex-start',
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    }}
  >
    {children}
  </div>
);

const StepperHeader = ({
  current,
  total,
}: {
  current: number;
  total: number;
}) => {
  const percent = Math.round((current / total) * 100);
  return (
    <div style={{ marginBottom: '16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '8px',
        }}
      >
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Progress
        </span>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
          {current}
          {' / '}
          {total}
        </span>
      </div>
      <div
        style={{
          width: '100%',
          height: '6px',
          background: '#f3f4f6',
          borderRadius: '999px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            background: '#E07B2D',
            borderRadius: '999px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

type StepState = 'completed' | 'active' | 'upcoming';

const stepStateColors = (state: StepState) => {
  if (state === 'completed') {
    return { bg: '#10b981', border: '#10b981', fg: '#fff', label: '#10b981' };
  }
  if (state === 'active') {
    return { bg: '#E07B2D', border: '#E07B2D', fg: '#fff', label: '#E07B2D' };
  }
  return { bg: '#fff', border: '#d1d5db', fg: '#9ca3af', label: '#6b7280' };
};

const StepItem = ({
  state,
  disabled,
  onClick,
  index,
  total,
  label,
  desc,
  optional,
}: {
  state: StepState;
  disabled: boolean;
  onClick: () => void;
  index: number;
  total: number;
  label: string;
  desc: string;
  optional?: boolean;
}) => {
  const colors = stepStateColors(state);
  const isLast = index === total - 1;
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '8px',
          width: '100%',
          background: state === 'active' ? '#FFF7ED' : 'transparent',
          border: 'none',
          borderRadius: '8px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          transition: 'background 0.2s',
          textAlign: 'left',
        }}
        onMouseEnter={(e) => {
          if (!disabled && state !== 'active') {
            (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb';
          }
        }}
        onMouseLeave={(e) => {
          if (state !== 'active') {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          }
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: colors.bg,
            color: colors.fg,
            border: `2px solid ${colors.border}`,
            fontSize: '13px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {state === 'completed' ? <Check size={14} strokeWidth={3} /> : index + 1}
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingTop: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
            <span
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: colors.label,
              }}
            >
              {label}
            </span>
            {optional && (
              <span
                style={{
                  padding: '1px 6px',
                  background: state === 'active' ? '#FED7AA' : '#f3f4f6',
                  color: state === 'active' ? '#92400E' : '#6b7280',
                  borderRadius: '4px',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                Optional
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: '12px',
              color: state === 'active' ? '#92400E' : '#6b7280',
              lineHeight: 1.4,
            }}
          >
            {desc}
          </div>
        </div>
      </button>
      {!isLast && (
        <div
          style={{
            position: 'absolute',
            left: '24px',
            top: '40px',
            bottom: '-4px',
            width: '2px',
            background: state === 'completed' ? '#10b981' : '#e5e7eb',
            transition: 'background 0.2s',
          }}
        />
      )}
    </div>
  );
};

const FormArea = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #e5e7eb' }}>
    {children}
  </div>
);

const NavigationButtons = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      padding: '16px 32px',
      background: '#fff',
      borderTop: '1px solid #e5e7eb',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.04)',
      zIndex: 30,
    }}
  >
    <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
      {children}
    </div>
  </div>
);

const NavButton = ({ variant = 'primary', disabled, onClick, children }: any) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: '10px 20px',
      background: variant === 'primary' ? (disabled ? '#f3f4f6' : '#E07B2D') : '#fff',
      color: variant === 'primary' ? (disabled ? '#9ca3af' : '#fff') : '#333',
      border: variant === 'primary' ? 'none' : '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s',
    }}
  >
    {children}
  </button>
);

export const AttractionWizard: FC<AttractionWizardProps> = ({
  onSubmit,
  initialValues,
  isEdit = false,
  isSubmitting = false,
}) => {
  const { languages: dbLanguages, baseLang } = useLangState();
  const { currentUser } = useAuthState();

  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);

  // Basic Info
  const [title, setTitle] = useState(initialValues?.title?.pl || '');
  const [description, setDescription] = useState(initialValues?.description?.pl || '');
  const [type, setType] = useState<AttractionType>(initialValues?.type || 'TRIP');
  const [recommended, setRecommended] = useState(initialValues?.recomended || false);

  // Address — backend stores the structured address under `addressData`
  // with `city_name`. Fall back to the legacy `address` shape used by the
  // create flow so this component works for both edit and create.
  const initialAddress = (initialValues as any)?.addressData ?? initialValues?.address;
  const [city, setCity] = useState<string>(
    initialAddress?.city_name ?? initialAddress?.city ?? '',
  );
  const [street, setStreet] = useState<string>(initialAddress?.street ?? '');
  const [number, setNumber] = useState<string>(initialAddress?.number ?? '');
  const [postalCode, setPostalCode] = useState<string>(initialAddress?.postal_code ?? '');

  // Geolocation
  const initialGeo = initialAddress?.geoPoint as
    | { latitude?: number; longitude?: number; _latitude?: number; _longitude?: number }
    | undefined;
  const initialLat = initialGeo?.latitude ?? initialGeo?._latitude;
  const initialLng = initialGeo?.longitude ?? initialGeo?._longitude;
  const [latitude, setLatitude] = useState<string>(
    typeof initialLat === 'number' ? initialLat.toString() : '',
  );
  const [longitude, setLongitude] = useState<string>(
    typeof initialLng === 'number' ? initialLng.toString() : '',
  );
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);

  // Contact
  const [email, setEmail] = useState(initialValues?.email || '');
  const [phone, setPhone] = useState(initialValues?.phone || '');
  const [webUrl, setWebUrl] = useState(initialValues?.web_url || '');
  const [isBuyTicketUrl, setIsBuyTicketUrl] = useState(
    (initialValues as any)?.isBuyTicketUrl || false,
  );

  // Media
  const [coverPhoto, setCoverPhoto] = useState(initialValues?.cover_photo || '');
  const [gallery, setGallery] = useState<string[]>(initialValues?.gallery || []);
  const [brandIcon, setBrandIcon] = useState(initialValues?.brand_icon || '');

  // Discount
  const [showDiscount, setShowDiscount] = useState(initialValues?.showDiscount || false);
  const [discountTitle, setDiscountTitle] = useState(
    initialValues?.discount?.title?.pl || '',
  );
  const [discountDescription, setDiscountDescription] = useState(
    initialValues?.discount?.description?.pl || '',
  );
  const [discountCode, setDiscountCode] = useState(
    initialValues?.discount?.discount_code || '',
  );
  const [discountValue, setDiscountValue] = useState(
    initialValues?.discount?.value?.toString() || '',
  );
  const [discountEndDate, setDiscountEndDate] = useState<any>(
    initialValues?.discount?.end_date || null,
  );

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
      }
    }
  }, [latitude, longitude]);

  const onMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      setLatitude(e.latLng.lat().toString());
      setLongitude(e.latLng.lng().toString());
      setMapCenter({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    }
  };

  const handleCoverPhotoUpload = useCallback((files: any[]) => {
    if (files.length > 0) {
      setCoverPhoto(files[0].fileUrl);
    }
  }, []);

  const handleGalleryUpload = useCallback((files: any[]) => {
    if (files.length > 0) {
      const newUrls = files.map((f) => f.fileUrl);
      setGallery((prev) => [...prev, ...newUrls].slice(0, 9));
    }
  }, []);

  const handleBrandIconUpload = useCallback((files: any[]) => {
    if (files.length > 0) {
      setBrandIcon(files[0].fileUrl);
    }
  }, []);

  const handleRemoveGalleryImage = useCallback((index: number) => {
    setGallery((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Stepper navigation
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0: // Basic Information
        return !!(title && description);
      case 1: // Location
        return !!(city && street);
      case 2: // Media
        return !!coverPhoto;
      case 3: // Discount (always valid - optional)
        return true;
      default:
        return false;
    }
  };

  const canProceed = isStepValid(currentStep);

  const handleNext = () => {
    if (currentStep < STEP_LABELS.length - 1 && canProceed) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Allow going back freely; allow jumping forward by one step only when
    // the current step is valid — same gate as the Next button.
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
      return;
    }
    if (stepIndex === currentStep + 1 && canProceed) {
      setCurrentStep(stepIndex);
    }
  };

  const isStepClickable = (stepIndex: number): boolean => {
    if (stepIndex <= currentStep) return true;
    if (stepIndex === currentStep + 1) return canProceed;
    return false;
  };

  const handleSubmit = () => {
    // Validate all required steps
    if (!title || !description || !city || !street || !coverPhoto) {
      alert('Please fill in all required fields');
      return;
    }

    const data: any = {
      title: { pl: title },
      description: { pl: description },
      address: {
        city_name: city,
        street,
        number,
        postal_code: postalCode,
        geoPoint: {
          latitude: parseFloat(latitude) || 0,
          longitude: parseFloat(longitude) || 0,
        },
      },
      cover_photo: coverPhoto,
      gallery: gallery && gallery.length > 0 ? gallery : [],
      type,
      recomended: recommended,
      languages: dbLanguages.length > 0 ? dbLanguages : [baseLang],
      showDiscount,
      status: 'LIVE' as 'PREVIEW' | 'LIVE',
      baseLang,
    };

    // Preserve creator on edit; set current user on create.
    const existingCreatedBy = (initialValues as any)?.createdBy;
    const fallbackUid = currentUser?.uid;
    if (existingCreatedBy) {
      data.createdBy = existingCreatedBy;
    } else if (fallbackUid) {
      data.createdBy = fallbackUid;
    }

    // Only add optional fields if they have values
    if (webUrl && webUrl.trim()) {
      data.web_url = webUrl.trim();
      data.isBuyTicketUrl = !!isBuyTicketUrl;
    }
    if (email && email.trim()) {
      data.email = email.trim();
    }
    if (phone && phone.trim()) {
      data.phone = phone.trim();
    }
    if (brandIcon && brandIcon.trim()) {
      data.brand_icon = brandIcon.trim();
    }

    if (showDiscount && discountTitle && discountDescription) {
      const discountData: any = {
        title: { pl: discountTitle.trim() },
        description: { pl: discountDescription.trim() },
      };

      if (discountCode && discountCode.trim()) {
        discountData.discount_code = discountCode.trim();
      }
      if (discountEndDate) {
        discountData.end_date = discountEndDate;
      }
      if (discountValue && !isNaN(parseFloat(discountValue))) {
        discountData.value = parseFloat(discountValue);
      }

      data.discount = discountData;
    }

    console.log('Submitting attraction data:', JSON.stringify(data, null, 2));
    onSubmit(data);
  };

  const isLastStep = currentStep === STEP_LABELS.length - 1;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Information
        return (
          <>
            <style>
              {`
                @keyframes spin {
                  from {
                    transform: rotate(0deg);
                  }
                  to {
                    transform: rotate(360deg);
                  }
                }
              `}
            </style>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>Basic Information</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Input
                {...inputProps}
                label="Title (Polish)"
                placeholder="Enter attraction title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                isRequired
              />
              <Textarea
                label="Description (Polish)"
                placeholder="Enter attraction description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                isRequired
                minRows={4}
              />
              <Select
                {...selectProps}
                label="Category"
                selectedKeys={new Set([type])}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as AttractionType;
                  if (selected) setType(selected);
                }}
                isRequired
              >
                {ATTRACTION_TYPES.map((t) => (
                  <SelectItem key={t.value}>{t.label}</SelectItem>
                ))}
              </Select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ fontSize: '14px' }}>Recommended</label>
                <Switch
                  isSelected={recommended}
                  onValueChange={setRecommended}
                />
              </div>

              <div style={{ marginTop: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Contact Information</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Input
                    {...inputProps}
                    type="email"
                    label="Email"
                    placeholder="contact@attraction.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Input
                    {...inputProps}
                    type="tel"
                    label="Phone"
                    placeholder="+48 123 456 789"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <Input
                    {...inputProps}
                    type="url"
                    label="Website URL"
                    placeholder="https://attraction.com"
                    value={webUrl}
                    onChange={(e) => setWebUrl(e.target.value)}
                  />
                  <Checkbox
                    isSelected={isBuyTicketUrl}
                    onValueChange={setIsBuyTicketUrl}
                    size="sm"
                  >
                    Buy ticket
                  </Checkbox>
                </div>
              </div>
            </div>
          </>
        );
      case 1: // Location
        return (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>Location</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Address</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <AddressAutocomplete
                    label="City"
                    placeholder="Enter city"
                    value={city}
                    onChange={setCity}
                    types={['(cities)']}
                    onPlaceSelect={(result) => {
                      setLatitude(String(result.latitude));
                      setLongitude(String(result.longitude));
                      setMapCenter({ lat: result.latitude, lng: result.longitude });
                    }}
                  />
                  <AddressAutocomplete
                    label="Street"
                    placeholder="Enter street name"
                    value={street}
                    onChange={setStreet}
                    types={['address']}
                    onPlaceSelect={(result) => {
                      setLatitude(String(result.latitude));
                      setLongitude(String(result.longitude));
                      setMapCenter({ lat: result.latitude, lng: result.longitude });
                    }}
                  />
                  <Input
                    {...inputProps}
                    label="Postal Code"
                    placeholder="00-000"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Geolocation</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <Input
                      {...inputProps}
                      label="Latitude"
                      placeholder="52.2297"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                    />
                    <Input
                      {...inputProps}
                      label="Longitude"
                      placeholder="21.0122"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                    />
                  </div>
                  <p style={{ fontSize: '12px', color: '#666' }}>
                    Drag the marker on the map to set coordinates automatically
                  </p>
                  {isLoaded && (
                    <div style={{ width: '100%', height: '350px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                      <GoogleMap
                        mapContainerStyle={MAP_CONTAINER_STYLE}
                        center={mapCenter}
                        zoom={15}
                        options={{
                          mapTypeControl: false,
                          fullscreenControl: false,
                          streetViewControl: false,
                        }}
                      >
                        <Marker
                          draggable
                          position={mapCenter}
                          onDragEnd={onMarkerDragEnd}
                        />
                      </GoogleMap>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        );
      case 2: // Media
        return (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>Media</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Cover Photo */}
              <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '8px', display: 'block' }}>
                  Cover Photo *
                </label>
                {coverPhoto ? (
                  <div style={{ position: 'relative', width: '100%', maxHeight: '280px', aspectRatio: '16/9', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                    <img
                      src={coverPhoto}
                      alt="Cover"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setCoverPhoto('')}
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        backdropFilter: 'blur(4px)',
                        border: 'none',
                        borderRadius: '6px',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
                    >
                      <Trash2 size={16} color="white" />
                    </button>
                  </div>
                ) : (
                  <UploadButton
                    options={{
                      apiKey: BYTESCALE_API_KEY,
                      maxFileCount: 1,
                      mimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
                      editor: { images: { crop: false } },
                      showFinishButton: true,
                      styles: {
                        colors: {
                          primary: '#E07B2D',
                        },
                      },
                    }}
                    onComplete={handleCoverPhotoUpload}
                  >
                    {({ onClick }) => (
                      <button
                        type="button"
                        onClick={onClick}
                        style={{
                          padding: '10px 20px',
                          background: '#E07B2D',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#C56A1F'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#E07B2D'}
                      >
                        <Upload size={16} />
                        Upload Cover Photo
                      </button>
                    )}
                  </UploadButton>
                )}
              </div>

              {/* Gallery */}
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '12px', display: 'block' }}>
                  Gallery
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 6px',
                    background: '#f3f4f6',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '400',
                  }}
                  >
                    {gallery.length}
/9
                  </span>
                </label>
                {gallery.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                  >
                    {gallery.map((url, idx) => (
                      <div
                        key={url}
                        style={{
                          position: 'relative',
                          width: '100%',
                          aspectRatio: '1',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #e5e7eb',
                        }}
                        onMouseEnter={(e) => {
                          const btn = e.currentTarget.querySelector('button');
                          if (btn) (btn as HTMLElement).style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          const btn = e.currentTarget.querySelector('button');
                          if (btn) (btn as HTMLElement).style.opacity = '0';
                        }}
                      >
                        <img
                          src={url}
                          alt={`Gallery ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveGalleryImage(idx)}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(239, 68, 68, 0.9)',
                            backdropFilter: 'blur(4px)',
                            border: 'none',
                            borderRadius: '6px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            opacity: 0,
                            transition: 'all 0.2s',
                          }}
                        >
                          <Trash2 size={14} color="white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {gallery.length < 9 && (
                  <UploadButton
                    options={{
                      apiKey: BYTESCALE_API_KEY,
                      maxFileCount: 9 - gallery.length,
                      mimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
                      editor: { images: { crop: false } },
                      showFinishButton: true,
                      styles: {
                        colors: {
                          primary: '#E07B2D',
                        },
                      },
                    }}
                    onComplete={handleGalleryUpload}
                  >
                    {({ onClick }) => (
                      <button
                        type="button"
                        onClick={onClick}
                        style={{
                          padding: '10px 20px',
                          background: '#E07B2D',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#C56A1F'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#E07B2D'}
                      >
                        <Plus size={16} />
                        Add Gallery Images (
{gallery.length}
/9)
                      </button>
                    )}
                  </UploadButton>
                )}
              </div>

              {/* Brand Icon */}
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '12px', display: 'block' }}>
                  Brand Icon / Logo
                </label>
                {brandIcon ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '100px',
                      height: '100px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fafafa',
                    }}
                    >
                      <img
                        src={brandIcon}
                        alt="Brand Icon"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <UploadButton
                        options={{
                          apiKey: BYTESCALE_API_KEY,
                          maxFileCount: 1,
                          mimeTypes: ['image/*'],
                          editor: { images: { crop: false } },
                          showFinishButton: true,
                          styles: {
                            colors: {
                              primary: '#10b981',
                            },
                          },
                        }}
                        onComplete={handleBrandIconUpload}
                      >
                        {({ onClick }) => (
                          <button
                            type="button"
                            onClick={onClick}
                            style={{
                              padding: '10px 20px',
                              background: '#10b981',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
                          >
                            <Edit size={16} />
                            Change Logo
                          </button>
                        )}
                      </UploadButton>
                      <button
                        type="button"
                        onClick={() => setBrandIcon('')}
                        style={{
                          padding: '8px 16px',
                          background: '#fff',
                          border: '1px solid #ef4444',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: '#ef4444',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <UploadButton
                    options={{
                      apiKey: BYTESCALE_API_KEY,
                      maxFileCount: 1,
                      mimeTypes: ['image/*'],
                      editor: { images: { crop: false } },
                      showFinishButton: true,
                      styles: {
                        colors: {
                          primary: '#E07B2D',
                        },
                      },
                    }}
                    onComplete={handleBrandIconUpload}
                  >
                    {({ onClick }) => (
                      <button
                        type="button"
                        onClick={onClick}
                        style={{
                          padding: '10px 20px',
                          background: '#E07B2D',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#C56A1F'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#E07B2D'}
                      >
                        <Upload size={16} />
                        Upload Brand Logo
                      </button>
                    )}
                  </UploadButton>
                )}
              </div>
            </div>
          </>
        );
      case 3: // Discount
        return (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>Discount Settings</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ fontSize: '14px' }}>Enable Discount</label>
                <Switch
                  isSelected={showDiscount}
                  onValueChange={setShowDiscount}
                />
              </div>

              {showDiscount && (
                <>
                  <Input
                    {...inputProps}
                    label="Discount Title (Polish)"
                    placeholder="e.g. Special Summer Offer"
                    value={discountTitle}
                    onChange={(e) => setDiscountTitle(e.target.value)}
                  />
                  <Textarea
                    label="Discount Description (Polish)"
                    placeholder="Describe the discount details"
                    value={discountDescription}
                    onChange={(e) => setDiscountDescription(e.target.value)}
                    minRows={3}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <Input
                      {...inputProps}
                      label="Discount Code"
                      placeholder="e.g. SUMMER2024"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                    />
                    <Input
                      {...inputProps}
                      type="number"
                      label="Discount Value (%)"
                      placeholder="e.g. 20"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout>
      <StepperCard>
        <StepperHeader current={currentStep + 1} total={STEP_LABELS.length} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {STEP_LABELS.map((step, index) => {
            let state: StepState = 'upcoming';
            if (currentStep === index) state = 'active';
            else if (currentStep > index) state = 'completed';

            return (
              <StepItem
                key={step.label}
                index={index}
                total={STEP_LABELS.length}
                state={state}
                disabled={!isStepClickable(index)}
                onClick={() => handleStepClick(index)}
                label={step.label}
                desc={step.desc}
                optional={step.optional}
              />
            );
          })}
        </div>
      </StepperCard>

      <FormArea>
        <ScrollShadow hideScrollBar className="max-h-[calc(100vh-200px)] pb-8">
          {renderStepContent()}
        </ScrollShadow>
      </FormArea>

      <NavigationButtons>
        <div>
          {currentStep > 0 && (
            <NavButton variant="secondary" onClick={handleBack}>
              <ArrowLeft size={16} />
              Back
            </NavButton>
          )}
        </div>
        <NavButton
          variant="primary"
          onClick={isLastStep ? handleSubmit : handleNext}
          disabled={(!canProceed && !isLastStep) || (isLastStep && isSubmitting)}
        >
          {isLastStep ? (
            <>
              {isSubmitting ? (
                <>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #fff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }}
                  />
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{isEdit ? 'Update Attraction' : 'Create Attraction'}</>
              )}
            </>
          ) : (
            <>
              Next
              <ArrowRight size={16} />
            </>
          )}
        </NavButton>
      </NavigationButtons>
    </WizardLayout>
  );
};

export default AttractionWizard;
